import { easeExpInOut } from 'd3-ease';
// tslint:disable-next-line: no-submodule-imports
import useStyles from 'isomorphic-style-loader/useStyles';
import React, { useEffect } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { animated, useSpring } from 'react-spring';
import { HIDE_PLAY_QUEUE } from '../actions';
import { VISUAL_TABLE_WIDGET } from '../types/templates/widgets/ISkyfireVisualTableWidget';
import { dispatchPlaybackMethods } from '../utils/dispatchPlaybackMethods';
import * as Styles from './PlayQueue.scss';
import Widget from './widgets';
import VisualTableWidget from './widgets/VisualTableWidget';
const headerStyles = {
    contain: 'layout',
    paddingTop: '20px',
    display: 'block',
    marginBottom: '0px',
};
export default function PlayQueue() {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m;
    useStyles(Styles);
    const dispatch = useDispatch();
    const { isPlayQueueOpen } = useSelector((state) => state.PlayQueue, shallowEqual);
    const { windowHeight } = useSelector((state) => state.BrowserState, shallowEqual);
    const { playQueue, onVisualPlayQueueDataRequired, mediaId } = useSelector((state) => state.Media);
    const { visualPlayQueue } = useSelector((state) => state.PlaybackStates);
    if (!visualPlayQueue) {
        return React.createElement("div", { style: { display: 'none' } });
    }
    const emptyFunction = () => true;
    const hasLoaded = (_b = (_a = playQueue) === null || _a === void 0 ? void 0 : _a.widgets) === null || _b === void 0 ? void 0 : _b.length;
    const hasNoUpcomingTracks = ((_f = (_e = (_d = (_c = playQueue) === null || _c === void 0 ? void 0 : _c.widgets) === null || _d === void 0 ? void 0 : _d[0]) === null || _e === void 0 ? void 0 : _e.items) === null || _f === void 0 ? void 0 : _f.length) === 0;
    const trackCountClassName = [!hasLoaded && Styles.countLoading].join(' ');
    const durationClassName = [!hasLoaded && Styles.durationLoading].join(' ');
    const PlayQueueAnimationValues = useSpring({
        opacity: isPlayQueueOpen ? 1 : 0,
        height: isPlayQueueOpen ? windowHeight - 80 : 0,
        config: { easing: easeExpInOut },
    });
    let timeoutHandle;
    // Check if PQ is open, disable root scroller to use PQ scroller.
    useEffect(() => {
        var _a;
        const windowScrollElement = document.documentElement;
        if (isPlayQueueOpen) {
            (_a = timeoutHandle) === null || _a === void 0 ? void 0 : _a.clear();
            windowScrollElement.style['overflow-y'] = 'hidden';
        }
        else {
            timeoutHandle = setTimeout(() => {
                windowScrollElement.style['overflow-y'] = 'scroll';
            }, 500);
        }
    }, [isPlayQueueOpen]);
    // If there's no data, fetch some. We need to fetch data, even if the PQ isn't open for
    // the time being to show the disabled state for the PQ Button until UX has a better
    // solution.
    useEffect(() => {
        if (!playQueue) {
            dispatchPlaybackMethods(dispatch, mediaId, onVisualPlayQueueDataRequired);
        }
    }, [mediaId, isPlayQueueOpen]);
    // Handle Escape key shortcut.
    useEffect(() => {
        window.addEventListener('keydown', handleEscape);
        return () => window.removeEventListener('keydown', handleEscape);
    }, []);
    // If there's no upcoming tracks, hide the Play Queue.
    useEffect(() => {
        if (hasNoUpcomingTracks && hasLoaded) {
            dispatch({ type: HIDE_PLAY_QUEUE });
        }
    }, [hasNoUpcomingTracks]);
    useEffect(() => {
        var _a;
        if (isPlayQueueOpen && ((_a = visualPlayQueue) === null || _a === void 0 ? void 0 : _a.isDisabled)) {
            dispatch({ type: HIDE_PLAY_QUEUE });
        }
    }, [(_g = visualPlayQueue) === null || _g === void 0 ? void 0 : _g.isDisabled]);
    return (React.createElement(animated.div, { style: PlayQueueAnimationValues, className: Styles.playqueue, id: "playqueue" },
        React.createElement("music-detail-header", { style: headerStyles, headline: (_h = playQueue) === null || _h === void 0 ? void 0 : _h.title, className: hasLoaded ? '' : Styles.hidden }),
        React.createElement("header", { className: hasLoaded ? Styles.hidden : Styles.headerLoading }),
        React.createElement("b", { className: Styles.subtitle },
            React.createElement("span", { className: trackCountClassName }, (_j = playQueue) === null || _j === void 0 ? void 0 : _j.trackCount),
            ((_k = playQueue) === null || _k === void 0 ? void 0 : _k.totalDuration) ? React.createElement("span", null, "\u2022") : null,
            React.createElement("span", { className: durationClassName }, convertToReadableTime((_l = playQueue) === null || _l === void 0 ? void 0 : _l.totalDuration))),
        React.createElement("div", { className: hasLoaded ? '' : Styles.hidden }, renderWidgets((_m = playQueue) === null || _m === void 0 ? void 0 : _m.widgets)),
        React.createElement("div", { className: hasLoaded ? Styles.hidden : '' },
            React.createElement(VisualTableWidget, { data: {
                    interface: VISUAL_TABLE_WIDGET,
                    items: Array(20).fill(1),
                    onEndOfWidget: [],
                    onTrackReorder: [],
                    onViewed: []
                }, handleSelected: emptyFunction, loading: true }))));
    function handleEscape(e) {
        if (e.keyCode === 27) {
            dispatch({ type: HIDE_PLAY_QUEUE });
        }
    }
    function convertToReadableTime(durationString) {
        if (!durationString) {
            return undefined;
        }
        const minutes = Number(durationString.substr(0, durationString.indexOf(':')));
        if (minutes) {
            return String(Math.floor(minutes / 60)) + 'hr ' + String(minutes % 60) + ' min';
        }
        return undefined;
    }
    function renderWidgets(widgetsToRender) {
        var _a;
        return (_a = widgetsToRender) === null || _a === void 0 ? void 0 : _a.map((widget) => {
            if (widget.interface === VISUAL_TABLE_WIDGET) {
                return (React.createElement(VisualTableWidget, { data: widget, handleSelected: handleSelected, isVisualPlayQueue: true }));
            }
            return React.createElement(Widget, { data: widget, handleSelected: handleSelected });
        });
    }
    function handleSelected(onItemSelected) {
        var _a;
        if (!((_a = onItemSelected) === null || _a === void 0 ? void 0 : _a.length)) {
            return;
        }
        dispatchPlaybackMethods(dispatch, mediaId, onItemSelected);
        dispatch({ type: HIDE_PLAY_QUEUE });
        dispatchPlaybackMethods(dispatch, mediaId, onVisualPlayQueueDataRequired);
    }
}
